import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  styled,
} from '@mui/material'
import { toast } from 'react-toastify'
import CloudUpload from '@mui/icons-material/CloudUpload'
import Save from '@mui/icons-material/Save'
import SendPrescriptionImages from 'src/clinic/components/ClinicManageBookingsPage/sendPrescriptionData/components/SendPrescriptionImages'
import { getDataWithParams, postDataWithParams } from 'src/consumer/services/profileService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getPrescriptionApiSignatures } from 'src/shared/utils/calendarReportApiHelper'

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#fff',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.9) 0%, rgba(99, 102, 241, 0.9) 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
  },
  '&.Mui-disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
}))

/**
 * UploadPrescriptionContent - Content for prescription image upload in the drawer
 */
export default function UploadPrescriptionContent({
  bookedEventInfo,
  mentorUserId,
  refreshBookedSlots,
  showPatientHistoryInUploadPrescriptionWidget,
  patientHistoryGetApiSignature,
  galleryImagesSearch,
  showGallery,
  fileUploadApiSignature,
  prescriptionImagesGetApiSignature,
  prescriptionImagesSaveApiSignature,
}) {
  // Get default prescription API signatures if not provided (portal-aware)
  const defaultPrescriptionApis = getPrescriptionApiSignatures()
  const finalGetApi = prescriptionImagesGetApiSignature || defaultPrescriptionApis.prescriptionImagesGetApiSignature
  const finalSaveApi = prescriptionImagesSaveApiSignature || defaultPrescriptionApis.prescriptionImagesSaveApiSignature
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [prescriptionImages, setPrescriptionImages] = useState([])

  console.log('UploadPrescriptionContent - API signatures:', {
    prescriptionImagesGetApiSignature: finalGetApi,
    prescriptionImagesSaveApiSignature: finalSaveApi,
  })

  useEffect(() => {
    if (bookedEventInfo?.id) {
      fetchPrescriptionImages()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookedEventInfo?.id])

  const fetchPrescriptionImages = async () => {
    if (!bookedEventInfo?.id) {
      setError('Invalid booking information')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const resp = await getDataWithParams(finalGetApi, {
        languageCode: 'en',
        platform: 'web',
        eventId: bookedEventInfo?.id,
      })

      if (resp.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          const respData = getDataMapFromCommonResponse(resp)
          setPrescriptionImages(respData?.prescriptionImages || [])
        } else {
          setError('Failed to load existing prescription images')
        }
      } else {
        setError('Network error. Please check your connection and try again.')
      }
    } catch (err) {
      console.error('Error fetching prescription images:', err)
      setError('An unexpected error occurred while loading images')
    } finally {
      setLoading(false)
    }
  }

  const savePrescription = async () => {
    if (!prescriptionImages || prescriptionImages.length === 0) {
      toast.warning('Please add at least one prescription image')
      return
    }

    if (!bookedEventInfo?.id) {
      toast.error('Invalid booking information')
      return
    }

    setSaving(true)
    setError(null)

    // Body is just the list of image strings
    const body = prescriptionImages

    const queryParams = {
      languageCode: 'en',
      platform: 'web',
      eventId: bookedEventInfo?.id,
      mentorId: bookedEventInfo?.mentorId,
      patientId: bookedEventInfo?.patientId,
    }

    try {
      const resp = await postDataWithParams(finalSaveApi, body, queryParams)

      if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        toast.success('Prescription images saved successfully')
        if (refreshBookedSlots) {
          refreshBookedSlots()
        }
      } else {
        setError('Failed to save prescription images. Please try again.')
        toast.error('Failed to save prescription images')
      }
    } catch (err) {
      console.error('Error saving prescription images:', err)
      setError('An unexpected error occurred while saving')
      toast.error('Failed to save prescription images')
    } finally {
      setSaving(false)
    }
  }

  const hasImages = prescriptionImages && prescriptionImages.length > 0

  return (
    <Box>
      {/* Header */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#4338ca', marginBottom: '8px' }}>
          Upload Prescription
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
          Upload prescription images for {bookedEventInfo?.userName}
        </Typography>
        {hasImages && (
          <Typography
            variant="caption"
            sx={{
              color: '#4338ca',
              fontWeight: 600,
              marginTop: '8px',
              display: 'block',
            }}
          >
            {prescriptionImages.length} {prescriptionImages.length === 1 ? 'image' : 'images'}{' '}
            uploaded
          </Typography>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ marginBottom: 2, borderRadius: '8px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Upload Component */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress size={40} sx={{ color: '#4338ca' }} />
        </Box>
      ) : (
        <SendPrescriptionImages
          bookedEventInfo={bookedEventInfo}
          setUploadPrescriptionImages={setPrescriptionImages}
          uploadPrescriptionImages={prescriptionImages}
          loading={loading}
          setLoading={setLoading}
          showGallery={showGallery}
          mentorUserId={mentorUserId}
          galleryImagesSearch={galleryImagesSearch}
          fileUploadApiSignature={fileUploadApiSignature}
        />
      )}

      {/* Save Button */}
      <Box
        sx={{
          marginTop: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: 2,
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <StyledButton
          variant="contained"
          onClick={savePrescription}
          disabled={saving || !hasImages}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: '200px' },
          }}
        >
          {saving ? 'Saving...' : 'Save Prescription Images'}
        </StyledButton>
      </Box>
    </Box>
  )
}

